import { EnvMode } from "@/lib/env/feature-flag";
import { QUERY_EVENTS } from "@/lib/events/constants";
import { emitEvent } from "@/lib/events/helpers";
import { pipe } from "effect";
import * as Effect from "effect/Effect";
import { LANGUAGE_CONFIG, type SupportedLanguage } from "../config/languages";
import {
  type INITIAL_TRANSLATION_RESOURCES,
  defaultNS,
  zodNS,
} from "../config/translations";
import { loadInitialLocale } from "../core/date-locale";
import { LanguageResolver } from "../core/languageResolver";
import { type InitOptions, i18next, initReactI18next, use } from "./i18next";
import { i18nextTranslationLoader } from "./i18nextTranslationLoader";

/**
 * i18nextの型定義を拡張
 * @see https://www.i18next.com/overview/typescript#custom-type-options
 */
declare module "i18next" {
  interface CustomTypeOptions {
    defaultNS: typeof defaultNS;
    resources: (typeof INITIAL_TRANSLATION_RESOURCES)["en-US"];
    returnNull: false;
  }
}

// i18nextの設定型を定義
type I18nConfig = Omit<InitOptions, "backend"> & {
  backend?: {
    loadPath?: string;
  };
};

/**
 * 言語設定を取得するためのプロバイダー関数型
 */
export interface LanguageProvider {
  sessionLocale?: SupportedLanguage | null;
  clientLocale?: SupportedLanguage | null;
}

/**
 * i18nextの初期化オプション
 * @see https://www.i18next.com/overview/configuration-options
 */
const createI18nOptions = (initialLanguage: SupportedLanguage): I18nConfig => ({
  lng: initialLanguage,
  fallbackLng: LANGUAGE_CONFIG["en-US"].code,
  load: "currentOnly" as const,
  debug: process.env.NODE_ENV === EnvMode.development,
  supportedLngs: Object.values(LANGUAGE_CONFIG).map((lang) => lang.code),
  partialBundledLanguages: true,
  ns: [defaultNS, zodNS],
  defaultNS: defaultNS,
  interpolation: {
    escapeValue: false, // 自動エスケープをOff（dangerouslySetInnerHTMLを利用されていない限りは安全 & biomeの設定でdangerouslySetInnerHTMLの利用を禁止しているので大丈夫）
  },
});

/**
 * i18nextの初期化
 *
 * @param options - 初期化オプション
 * @param languageProvider - 言語設定プロバイダー
 */
export async function initializeI18n(
  options: I18nInitOptions = {},
  languageProvider?: LanguageProvider,
) {
  try {
    const browserEnv: BrowserEnv = {
      ...createDefaultBrowserEnv(),
    };

    const initialLanguage = LanguageResolver.resolve({
      sessionLocale: options.lng ?? languageProvider?.sessionLocale,
      clientLocale: languageProvider?.clientLocale,
      browserLanguage: browserEnv.language,
      supportedLanguages: LANGUAGE_CONFIG,
    }) as SupportedLanguage;

    browserEnv.setHtmlLang(initialLanguage);
    await loadInitialLocale(initialLanguage);
    const i18nOptions: I18nConfig = createI18nOptions(initialLanguage);

    await use(initReactI18next).use(i18nextTranslationLoader).init(i18nOptions);

    // 言語変更イベントのリスナーを設定する前に既存のリスナーを削除
    i18next.off("languageChanged");
    i18next.on("languageChanged", handleLanguageChange(browserEnv));

    return { success: true };
  } catch (error) {
    console.error("Failed to initialize i18n:", error);
    return { success: false, error };
  }
}

// 初期化オプションの型を定義
export interface I18nInitOptions {
  lng?: string;
}

export interface BrowserEnv {
  language: string;
  setHtmlLang: (lang: string) => void;
  readonly isServer: boolean;
}

// デフォルトのブラウザ環境
const createDefaultBrowserEnv = (): BrowserEnv => {
  const isServer =
    typeof window === "undefined" || typeof document === "undefined";
  const defaultEnv: BrowserEnv = {
    language: "en-US",
    setHtmlLang: () => void 0,
    isServer: true,
  };

  if (isServer) {
    return defaultEnv;
  }

  try {
    return {
      language: navigator?.language ?? defaultEnv.language,
      setHtmlLang: (lang: string) => {
        try {
          if (document?.documentElement) {
            document.documentElement.lang = lang;
          }
        } catch (error) {
          console.warn("Failed to set HTML lang attribute:", error);
        }
      },
      isServer: false,
    };
  } catch (error) {
    console.warn("Failed to create browser environment:", error);
    return defaultEnv;
  }
};

/**
 * 言語変更時の副作用を処理
 * router依存を削除してイベントベースに変更
 */
const handleLanguageChange = (browserEnv: BrowserEnv) => (lng: string) =>
  pipe(
    Effect.tryPromise({
      try: async () => {
        if (browserEnv.isServer) {
          return;
        }
        try {
          if (typeof browserEnv.setHtmlLang === "function") {
            browserEnv.setHtmlLang(lng);
          }

          // date-fnsのロケール読み込み
          const dateLocaleResult = await Promise.allSettled([
            loadInitialLocale(lng as SupportedLanguage),
          ]);

          // 個別の結果をチェック
          if (dateLocaleResult[0].status === "rejected") {
            console.error(
              "Failed to load date locale:",
              dateLocaleResult[0].reason,
            );
          }

          emitEvent(QUERY_EVENTS.INVALIDATE_ACTIVE, {
            reason: "languageChanged",
            language: lng,
          });
        } catch (error) {
          console.error("Error in language change handler:", error);
        }
      },
      catch: (error) => {
        console.error("Error handling language change:", error);
        return Effect.succeed(void 0);
      },
    }),
    Effect.runPromise,
  );
